import React, { useState, useRef, Children, createElement } from 'react';
import { TrendingUpIcon, UsersIcon, ShieldCheckIcon, ActivityIcon, BarChart3Icon, NewspaperIcon, UtensilsIcon, BikeIcon, CoinsIcon, ArrowRightIcon, LightbulbIcon, SparklesIcon, TvIcon, DownloadIcon, CheckCircleIcon, WineIcon, ChevronDownIcon, ChevronUpIcon, PlayIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Label, ReferenceArea } from 'recharts';
import { ConfidenceNotification } from './ConfidenceNotification';
import { ShareButton } from './ShareButton';
import { LearnMore } from './LearnMore';
import { MethodologyAccordion } from './MethodologyAccordion';
import { DemographicCharts } from './DemographicCharts';
import { timelineData, audienceSegments, publishers, metrics, audiencePersonas } from '../utils/atlResonanceData';
export const ATLResonanceDashboard: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activePhase, setActivePhase] = useState<'baseline' | 'flight' | 'post-flight' | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{
    News: boolean;
    Food: boolean;
    Sport: boolean;
    Radio: boolean;
    Finance: boolean;
  }>({
    News: false,
    Food: false,
    Sport: false,
    Radio: false,
    Finance: false
  });
  // Gender composition data
  const genderData = [{
    label: 'Male',
    value: 53,
    color: '#C00000'
  }, {
    label: 'Female',
    value: 47,
    color: '#AAAAAA'
  }];
  // Age composition data
  const ageData = [{
    label: '35-44',
    value: 16,
    color: '#E5E5E5'
  }, {
    label: '45-54',
    value: 14,
    color: '#AAAAAA'
  }, {
    label: '55-64',
    value: 22,
    color: '#AAAAAA'
  }, {
    label: '65+',
    value: 25,
    color: '#C00000'
  }, {
    label: 'Unknown',
    value: 23,
    color: '#F9FAFB'
  }];
  const toggleCategory = (category: 'News' | 'Food' | 'Sport' | 'Radio' | 'Finance') => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-[#FFFFFF] p-3 border border-[#E5E5E5] rounded-[4px]" data-id="element-5">
          <p className="text-sm font-bold text-[#101010]" data-id="element-6">{label}</p>
          <p className="text-sm text-[#C00000] mt-1" data-id="element-7">
            Index: <span className="font-bold" data-id="element-8">{payload[0].value}</span>
          </p>
          <p className="text-xs text-[#AAAAAA] mt-1" data-id="element-9">
            {payload[0].payload.phase}
          </p>
        </div>;
    }
    return null;
  };
  const downloadChart = async () => {
    if (!chartRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = 'campaign-timeline.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
  };
  return <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10">
      {/* Header Section */}
      <div id="overview" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:mb-8" data-id="element-11">
        <div className="widgetsTitle" data-id="element-12">
          <span className="text-xs font-semibold text-[#C00000] uppercase tracking-wider mb-2 block" data-id="element-13">
            MEASUREMENT REPORT
          </span>
          <h1 className="title text-3xl md:text-[48px] font-bold text-[#171717]" style={{
          letterSpacing: '-1.44px',
          lineHeight: 1
        }} data-id="element-14">
            ATL Resonance Report: Lenovo
          </h1>
        </div>
        <div className="self-start sm:self-auto flex items-center bg-[#C00000]/10 text-[#C00000] px-4 py-2 rounded-full print:hidden" data-id="element-15">
          <ActivityIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-16" />
          <span className="text-sm font-semibold whitespace-nowrap" data-id="element-17">
            Campaign Status: Completed
          </span>
        </div>
      </div>

      {/* Test Overview */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5]" data-id="element-18">
        <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-19">
          <div className="space-y-4 flex-1" data-id="element-20">
            <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-21">
              Test Overview
            </h2>
            <p className="text-base text-[#000000] leading-relaxed" data-id="element-22">
              This report details the findings of an ATL Resonance test
              conducted by Brandpulse™ for Lenovo. We observed{' '}
              <span className="font-bold text-[#171717]" data-id="element-23">
                279,000+ unique individuals
              </span>{' '}
              from your target audience across three distinct phases: before,
              during, and after your TV campaign. Using a constant neutral
              stimulus, we measured how TV media pressure influenced behavioral
              engagement. The conclusion is positive: We measured a validated
              uplift of <span className="font-bold text-[#C00000]" data-id="element-24">+33%</span>{' '}
              during the campaign (97.5% confidence). Post-flight data shows
              brand interest elevated at{' '}
              <span className="font-bold text-[#C00000]" data-id="element-25">
                +14% above baseline
              </span>
              , a promising directional signal of sustained impact that warrants
              continued monitoring. Where traditional surveys ask a small group
              for their opinion, Brandpulse measures the actual behavior of the
              entire target audience, providing certainty that the campaign
              successfully activated the market and drove measurable brand lift.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Key Takeaways Summary Box */}
      <motion.div variants={itemVariants} className="relative overflow-hidden bg-[#000000] rounded-[4px] print:bg-white print:border print:border-[#E5E5E5] print:text-[#101010]" data-id="element-26">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-[#C00000]" data-id="element-27" />

        <div className="p-8 md:p-12" data-id="element-28">
          <div className="flex items-center gap-3 mb-4" data-id="element-29">
            <div className="h-px w-8 bg-[#C00000]" data-id="element-30" />
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#C00000] print:text-[#C00000]" data-id="element-31">
              Key Takeaways
            </p>
          </div>
          <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight text-white mb-10 max-w-2xl print:text-[#101010]" data-id="element-32">
            What the data tells us.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6" data-id="element-33">
            {['Campaign generated +33% uplift in brand interest (97.5% confidence)', 'Effect sustained 2 weeks post-flight at +14% (directional evidence)', 'Strongest with pension planning audience (Index 250)', 'Observed 279,000+ unique individuals across three measurement phases'].map((text, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4 + idx * 0.1
          }} className="flex items-start gap-3 border-t border-white/10 pt-5 print:border-[#E5E5E5]" data-id="element-34">
                <CheckCircleIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" data-id="element-35" />
                <p className="text-base text-[#E5E5E5] leading-relaxed print:text-[#000000]" data-id="element-36">
                  {text.split('+').map((part, i) => i === 0 ? part : <span key={i} data-id="element-37">
                        <span className="font-bold text-white print:text-[#101010]" data-id="element-38">
                          +{part.split(' ')[0]}
                        </span>
                        {part.substring(part.indexOf(' '))}
                      </span>)}
                </p>
              </motion.div>)}
          </div>
        </div>
      </motion.div>

      {/* Info Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-39">
        {[{
        icon: BarChart3Icon,
        title: 'Test type',
        value: 'ATL Resonance'
      }, {
        icon: ActivityIcon,
        title: 'Period',
        periods: [{
          label: 'Baseline',
          date: 'Dec 9 - 14'
        }, {
          label: 'Flight',
          date: 'Dec 15 - 31'
        }, {
          label: 'Post-Flight',
          date: 'Jan 1 - 7'
        }]
      }, {
        icon: ShieldCheckIcon,
        title: 'Completion date',
        value: 'January 7, 2026'
      }].map((item, idx) => <motion.div key={idx} variants={itemVariants} className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] h-full flex flex-col" data-id="element-40">
            <div className="flex items-center gap-3 mb-4" data-id="element-41">
              <item.icon className="h-5 w-5 text-[#C00000] flex-shrink-0" data-id="element-42" />
              <h3 className="text-base font-bold text-[#101010]" data-id="element-43">
                {item.title}
              </h3>
            </div>
            {'periods' in item ? <div className="space-y-2.5" data-id="element-44">
                {item.periods.map((period, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-45">
                    <span className="text-[#AAAAAA]" data-id="element-46">{period.label}</span>
                    <span className="font-medium text-[#101010]" data-id="element-47">
                      {period.date}
                    </span>
                  </div>)}
              </div> : <p className="text-base text-[#000000]" data-id="element-48">{item.value}</p>}
          </motion.div>)}
      </div>

      {/* Commercial Video */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5]" data-id="element-49">
        <div className="flex items-center gap-3 mb-8" data-id="element-50">
          <TvIcon className="h-6 w-6 text-[#C00000]" data-id="element-51" />
          <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-52">
            TV Commercial — Maximum David
          </h2>
        </div>

        {/* Campaign Overview */}
        <div className="mb-8 w-full" data-id="element-53">
          <div className="mb-3 inline-flex items-center gap-2" data-id="element-54">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#C00000]" data-id="element-55" />
            <span className="text-xs font-bold uppercase tracking-wider text-[#C00000]" data-id="element-56">
              About the campaign
            </span>
          </div>
          <h3 className="mb-4 text-lg md:text-xl font-bold text-[#101010]" data-id="element-57">
            Maximum David
          </h3>
          <div className="space-y-4 text-sm leading-relaxed text-[#404040]" data-id="element-58">
            <p data-id="element-59">
              <span className="font-semibold text-[#101010]" data-id="element-60">
                Maximum David
              </span>{' '}
              is Lenovo&rsquo;s global campaign showcasing how AI-driven
              technology elevates the creativity, performance, and impact of
              David Beckham — Inter Miami CF co-owner, entrepreneur, and global
              cultural figure — while spotlighting Lenovo&rsquo;s AI portfolio
              across devices, solutions, and services.
            </p>
            <p data-id="element-61">
              Launching one month before FIFA World Cup 2026&trade;, it builds
              on Lenovo&rsquo;s role as Official Technology Partner of FIFA
              World Cup 2026&trade; and FIFA Women&rsquo;s World Cup
              2027&trade;, deploying AI to support smarter operations, richer
              fan experiences, and sharper performance insights.
            </p>
          </div>
        </div>

        <div className="w-full" data-id="element-62">
          <div className="relative w-full rounded-[4px] overflow-hidden bg-[#F9FAFB] border border-[#E5E5E5]" style={{
          paddingBottom: '56.25%'
        }} data-id="element-63">
            {isVideoPlaying ? <video src="https://media.lenovonews.fiestic.com/2026/05/ZLVO4505_Beckham_Brand_Lenovo-FIFA_VH_1920x1080_EN_30.mp4" poster="https://cdn.magicpatterns.com/uploads/2fti7XQ3uSm5UJJx7R7Ejz/ZLVO4424_Beckham_Brand2_EN_SI_5000x3892-scaled.jpg" controls autoPlay playsInline className="absolute inset-0 w-full h-full object-cover bg-black" data-id="element-64">
                Your browser does not support the video tag.
              </video> : <>
                <img src="https://cdn.magicpatterns.com/uploads/2fti7XQ3uSm5UJJx7R7Ejz/ZLVO4424_Beckham_Brand2_EN_SI_5000x3892-scaled.jpg" alt="Campaign footage — Lenovo ThinkPad X1 brand film featuring David Beckham" className="absolute inset-0 w-full h-full object-cover" data-id="element-65" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-66">
                  <button type="button" onClick={() => setIsVideoPlaying(true)} aria-label="Play campaign video" className="flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-full px-6 py-3 cursor-pointer hover:bg-black/80 transition-colors" data-id="element-67">
                    <span className="text-white font-bold text-lg" data-id="element-68">
                      Play video
                    </span>
                    <div className="bg-[#C00000] rounded-full p-1.5" data-id="element-69">
                      <PlayIcon className="h-4 w-4 text-white fill-white" data-id="element-70" />
                    </div>
                  </button>
                </div>
              </>}
            {!isVideoPlaying && <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm" data-id="element-71">
                <p className="text-xs font-medium text-white" data-id="element-72">
                  Campaign footage — national TV flight Lenovo
                </p>
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Grid */}
      <div id="metrics" className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-73">
        {[{
        icon: UsersIcon,
        title: 'Unique Individuals',
        value: metrics.humanSignals.toLocaleString(),
        subtitle: 'Observed across all phases',
        plainText: 'Real people observed, not survey respondents',
        border: 'border-[#E5E5E5]',
        learnMoreTitle: 'What are Unique Individuals?',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-74">
                  Unique Individuals represent verified, distinct people
                  observed in their natural viewing environment across our
                  premium publisher network. This is the total number of people
                  we analyzed across the baseline, flight, and post-flight
                  periods.
                </p>
                <p className="mb-3" data-id="element-75">
                  <span className="font-bold" data-id="element-76">Important distinction:</span>{' '}
                  These are not "people who saw your TV ad" (we cannot track
                  that). These are individuals from your target audience whose
                  behavioral responses we measured using a constant neutral
                  stimulus across all three phases.
                </p>
                <p data-id="element-77">
                  Where traditional surveys ask a small group for their opinion,
                  Brandpulse observes the actual behavior of 279,000+
                  individuals, providing statistical power to detect even subtle
                  shifts in brand engagement.
                </p>
              </>
      }, {
        icon: TrendingUpIcon,
        iconColor: 'text-[#C00000]',
        title: 'Resonance Score',
        value: `+${metrics.uplift}%`,
        subtitle: '↑ VS industry benchmark (8-15%)',
        subtitleColor: 'text-[#C00000]',
        plainText: '33% more brand engagement vs. baseline (100) during campaign',
        border: 'border-[#C00000]',
        borderWidth: 'border-2',
        highlight: true,
        learnMoreTitle: 'Understanding the Uplift',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-78">
                  A +33% uplift means your campaign generated 33% more brand
                  engagement compared to the baseline period. This is measured
                  through actual behavioral responses to a constant neutral
                  stimulus across all measurement phases.
                </p>
                <p className="mb-3" data-id="element-79">
                  <span className="font-bold" data-id="element-80">What is the baseline?</span> We
                  compare the baseline response rate (pre-campaign) against the
                  flight response rate (during campaign) to calculate the pure
                  behavioral lift caused by your TV media pressure. The baseline
                  (Index 100) represents normal, pre-campaign engagement levels.
                </p>
                <p className="mb-3" data-id="element-81">
                  <span className="font-bold" data-id="element-82">Industry Context:</span> Research
                  shows typical TV campaigns achieve 8-15% brand awareness lift.
                  Your +33% uplift is 2.2x above the industry average, placing
                  this campaign in the top performance tier and demonstrating
                  exceptional effectiveness.
                </p>
                <p data-id="element-83">
                  <span className="font-bold" data-id="element-84">Think of it this way:</span> The
                  constant stimulus is like a thermometer measuring temperature.
                  Your TV campaign is the heat source. The uplift shows how much
                  your TV ads "heated up" brand interest in the market.
                </p>
              </>
      }, {
        icon: ShieldCheckIcon,
        title: 'Lift Certainty',
        value: `${metrics.confidence}%`,
        subtitle: 'for campaign-period uplift',
        plainText: "We're 97.5% certain your campaign caused the +33% uplift",
        border: 'border-[#E5E5E5]',
        learnMoreTitle: 'Understanding Confidence Levels',
        learnMoreContent: <>
                <p className="mb-3" data-id="element-85">
                  Using Bayesian statistical analysis, we can say with{' '}
                  <span className="font-bold" data-id="element-86">97.5% certainty</span> that your
                  TV campaign caused the observed +33% uplift during the flight
                  period, rather than random chance or external factors. This is
                  a very high level of confidence, giving you strong evidence
                  that your TV investment directly drove measurable results.
                </p>
                <p className="mb-3" data-id="element-87">
                  <span className="font-bold" data-id="element-88">Campaign Period (+33%):</span>{' '}
                  97.5% confidence, statistically validated and board-ready.
                </p>
                <p className="mb-3" data-id="element-89">
                  <span className="font-bold" data-id="element-90">Post-Flight Period (+14%):</span>{' '}
                  Strong directional evidence, but the 2-week observation window
                  is shorter than ideal for statistical validation (larger
                  margin of error).
                </p>
                <p className="text-xs text-[#000000] bg-[#F9FAFB] p-3 rounded-[4px] border border-[#E5E5E5]" data-id="element-91">
                  <span className="font-bold" data-id="element-92">For future tests:</span> Extending
                  the post-flight observation to 3-4 weeks would provide the
                  same 97.5% confidence for sustained impact claims. This can be
                  configured when setting up your next ATL Resonance test.
                </p>
              </>
      }].map((metric, idx) => <motion.div key={idx} variants={itemVariants} className={`bg-[#FFFFFF] p-6 rounded-[4px] ${metric.borderWidth || 'border'} ${metric.border} print:border print:border-[#E5E5E5] flex flex-col h-full`} data-id="element-93">
            <div className="flex items-center gap-3 mb-4" data-id="element-94">
              <metric.icon className={`h-5 w-5 ${metric.iconColor || 'text-[#AAAAAA]'} flex-shrink-0`} data-id="element-95" />
              <h3 className="text-base font-bold text-[#101010]" data-id="element-96">
                {metric.title}
              </h3>
            </div>
            <motion.p initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5 + idx * 0.1,
          type: 'spring',
          stiffness: 150
        }} className={`text-3xl md:text-4xl font-bold ${metric.highlight ? 'text-[#C00000]' : 'text-[#101010]'} mb-2`} data-id="element-97">
              {metric.value}
            </motion.p>
            <p className={`text-sm ${metric.subtitleColor || 'text-[#AAAAAA]'} mb-2`} data-id="element-98">
              {metric.subtitle && metric.subtitle.includes('•') ? <>
                  {metric.subtitle.split('•')[0]}
                  <span className="text-[#AAAAAA]" data-id="element-99">
                    • {metric.subtitle.split('•')[1]}
                  </span>
                </> : metric.subtitle}
            </p>
            <p className="text-xs text-[#AAAAAA] italic mb-4 flex-grow" data-id="element-100">
              In plain terms: {metric.plainText}
            </p>
            <div className="mt-auto pt-4 border-t border-[#E5E5E5]" data-id="element-101">
              <LearnMore title={metric.learnMoreTitle} data-id="element-102">
                {metric.learnMoreContent}
              </LearnMore>
            </div>
          </motion.div>)}
      </div>

      {/* Timeline Visualization */}
      <motion.div id="timeline" variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] print:break-inside-avoid" data-id="element-103">
        <div className="mb-6" data-id="element-104">
          <p className="text-sm text-[#AAAAAA] leading-relaxed" data-id="element-105">
            <span className="font-bold text-[#101010]" data-id="element-106">Timeline Analysis:</span>{' '}
            Visualizing the correlation between media flight dates and brand
            interest peaks.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-6" data-id="element-107">
          <div data-id="element-108">
            <h2 className="text-xl md:text-2xl font-bold text-[#101010] mb-2" data-id="element-109">
              The Heartbeat: Campaign Timeline
            </h2>
            <p className="text-sm text-[#AAAAAA]" data-id="element-110">
              Visualizing user interest before, during, and after the TV flight
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-111">
            <button onClick={downloadChart} className="flex items-center gap-2 text-sm text-[#AAAAAA] hover:text-[#C00000] transition-colors print:hidden self-start sm:self-auto px-4 py-2 rounded-sm hover:bg-[#F9FAFB]" type="button" data-id="element-112">
              <DownloadIcon className="h-4 w-4" data-id="element-113" />
              <span data-id="element-114">Download Chart</span>
            </button>
            <div className="flex flex-wrap items-center gap-4 text-sm print:space-x-3" data-id="element-115">
              {[{
              phase: 'baseline',
              label: 'Baseline (Dec 9 - 14)',
              color: 'bg-[#AAAAAA]'
            }, {
              phase: 'flight',
              label: 'Flight (Dec 15 - 31)',
              color: 'bg-[#C00000]'
            }, {
              phase: 'post-flight',
              label: 'Post-Flight (Jan 1 - 7)',
              color: 'bg-[#C00000]/40'
            }].map(({
              phase,
              label,
              color
            }) => <div key={phase} className="flex items-center cursor-pointer transition-opacity duration-200 print:cursor-default" style={{
              opacity: activePhase === null || activePhase === phase ? 1 : 0.3
            }} onMouseEnter={() => setActivePhase(phase as any)} onMouseLeave={() => setActivePhase(null)} data-id="element-116">
                  <div className={`w-3 h-3 ${color} rounded-full mr-2 flex-shrink-0`} data-id="element-117"></div>
                  <span className="text-[#AAAAAA] font-medium whitespace-nowrap" data-id="element-118">
                    {label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        <div ref={chartRef} className="h-[350px] md:h-[450px] w-full relative print:h-[350px]" data-id="element-119">
          <div className="absolute inset-0 pointer-events-none z-10 print:hidden" data-id="element-120">
            <div className="h-full flex" data-id="element-121">
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '20%'
            }} onMouseEnter={() => setActivePhase('baseline')} onMouseLeave={() => setActivePhase(null)} data-id="element-122" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '56.67%'
            }} onMouseEnter={() => setActivePhase('flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-123" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '23.33%'
            }} onMouseEnter={() => setActivePhase('post-flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-124" />
            </div>
          </div>

          <div className="absolute top-[58%] right-[8%] z-20 hidden md:block pointer-events-none" data-id="element-125">
            <span className="text-[#C00000] font-bold text-sm bg-[#FFFFFF]/90 px-2 py-1 rounded-[4px] border border-[#C00000]/20" data-id="element-126">
              +14% sustained
            </span>
          </div>

          <ResponsiveContainer width="100%" height="100%" data-id="element-127">
            <AreaChart data={timelineData} margin={{
            top: 20,
            right: 10,
            left: -10,
            bottom: 0
          }} data-id="element-128">
              <defs data-id="element-129">
                <linearGradient id="colorIndex" x1="0" y1="0" x2="0" y2="1" data-id="element-130">
                  <stop offset="5%" stopColor="#C00000" stopOpacity={0.3} data-id="element-131" />
                  <stop offset="95%" stopColor="#C00000" stopOpacity={0} data-id="element-132" />
                </linearGradient>
                <linearGradient id="colorIndexHighlight" x1="0" y1="0" x2="0" y2="1" data-id="element-133">
                  <stop offset="5%" stopColor="#C00000" stopOpacity={0.5} data-id="element-134" />
                  <stop offset="95%" stopColor="#C00000" stopOpacity={0.1} data-id="element-135" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" data-id="element-136" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
              fill: '#AAAAAA',
              fontSize: 11,
              fontWeight: 500
            }} dy={10} interval="preserveStartEnd" data-id="element-137" />
              <YAxis domain={[80, 160]} axisLine={false} tickLine={false} tick={{
              fill: '#AAAAAA',
              fontSize: 11,
              fontWeight: 500
            }} label={{
              value: 'Interest Index',
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#AAAAAA',
                fontSize: 11,
                fontWeight: 500
              }
            }} data-id="element-138" />
              <RechartsTooltip content={<CustomTooltip data-id="element-140" />} data-id="element-139" />

              {activePhase === 'baseline' && <ReferenceArea x1="Dec 9" x2="Dec 15" y1={80} y2={160} fill="#AAAAAA" fillOpacity={0.15} stroke="none" data-id="element-141" />}
              {activePhase === 'flight' && <ReferenceArea x1="Dec 15" x2="Jan 1" y1={80} y2={160} fill="#C00000" fillOpacity={0.12} stroke="none" data-id="element-142" />}
              {activePhase === 'post-flight' && <ReferenceArea x1="Jan 1" x2="Jan 7" y1={80} y2={160} fill="#C00000" fillOpacity={0.15} stroke="none" data-id="element-143" />}

              <ReferenceLine x="Dec 15" stroke="#AAAAAA" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-144">
                <Label value="Launch" position="top" fill="#AAAAAA" fontSize={10} fontWeight={600} data-id="element-145" />
              </ReferenceLine>
              <ReferenceLine x="Jan 1" stroke="#AAAAAA" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-146">
                <Label value="End" position="top" fill="#AAAAAA" fontSize={10} fontWeight={600} data-id="element-147" />
              </ReferenceLine>
              <ReferenceLine y={100} stroke="#C00000" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-148">
                <Label value="Baseline" position="insideTopRight" fill="#C00000" fontSize={10} fontWeight={600} data-id="element-149" />
              </ReferenceLine>

              <ReferenceArea x1="Dec 15" x2="Dec 31" y1={100} y2={133} fill="transparent" stroke="none" data-id="element-150">
                <Label value="+33%" position="center" fill="#C00000" fontSize={16} fontWeight="bold" data-id="element-151" />
              </ReferenceArea>

              <Area type="monotone" dataKey="index" stroke="#C00000" strokeWidth={3} fillOpacity={1} fill={activePhase ? 'url(#colorIndexHighlight)' : 'url(#colorIndex)'} animationDuration={2000} baseValue={100} data-id="element-152" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-6 p-6 bg-[#C00000]/5 rounded-[4px] border border-[#C00000]/20 print:hidden" data-id="element-153">
          <div className="flex items-center gap-3 mb-4" data-id="element-154">
            <LightbulbIcon className="h-5 w-5 text-[#C00000]" data-id="element-155" />
            <h3 className="text-lg font-bold text-[#101010]" data-id="element-156">Key Insights</h3>
          </div>

          <div className="space-y-4" data-id="element-157">
            {[{
            icon: TrendingUpIcon,
            color: 'text-[#C00000]',
            bg: 'bg-[#FFFFFF]',
            border: 'border-[#E5E5E5]',
            title: 'Validated Campaign Uplift: +33% (97.5% Confidence)',
            text: 'The campaign generated a statistically validated +33% uplift in brand engagement during the active flight period (Dec 15 - Jan 1). This represents board-ready proof that your TV investment drove measurable market activation. Industry research shows typical TV campaigns achieve 8-15% brand awareness lift. Your +33% uplift is 2.2x above the industry average, placing this campaign in the top performance tier.'
          }, {
            icon: SparklesIcon,
            color: 'text-[#C00000]',
            bg: 'bg-[#F9FAFB]',
            border: 'border-[#E5E5E5]',
            title: 'Instant Traction: Creative Validation',
            text: 'We detected zero "warm-up period". The curve spiked immediately at the start of the flight. This validates that the creative message was extremely clear and urgent to the target audience, a strong compliment to the creative execution.'
          }, {
            icon: CoinsIcon,
            color: 'text-[#AAAAAA]',
            bg: 'bg-[#F9FAFB]',
            border: 'border-[#E5E5E5]',
            title: 'Promising Sustained Effect: +14% (Directional Evidence)',
            text: 'The campaign delivered more than just temporary activation. We observe brand engagement sustained for 2 weeks at +14% above baseline, a promising indicator of lasting brand equity.',
            note: 'This post-flight effect represents strong directional evidence but has not yet reached statistical validation due to the shorter 2-week observation window. Extending post-flight measurement to 3-4 weeks in future tests would provide validated certainty on sustained impact.'
          }].map((insight, idx) => <div key={idx} className={`p-5 ${insight.bg} rounded-[4px] border ${insight.border}`} data-id="element-158">
                <div className="flex items-start gap-4" data-id="element-159">
                  <insight.icon className={`h-5 w-5 ${insight.color} flex-shrink-0 mt-0.5`} data-id="element-160" />
                  <div className="flex-1" data-id="element-161">
                    <p className="text-base font-bold text-[#101010] mb-2" data-id="element-162">
                      {insight.title}
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed mb-3" data-id="element-163">
                      {insight.text}
                    </p>
                    {insight.note && <p className="text-xs text-[#000000] bg-[#FFFFFF] p-3 rounded-[4px] border border-[#E5E5E5]" data-id="element-164">
                        <span className="font-bold" data-id="element-165">Note:</span> {insight.note}
                      </p>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </motion.div>

      {/* Methodology Accordion */}
      <MethodologyAccordion data-id="element-166" />

      {/* Study Scope: Analyzed Demographics */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5]" data-id="element-167">
        <div className="mb-8" data-id="element-168">
          <h2 className="text-xl md:text-2xl font-bold text-[#101010] mb-2" data-id="element-169">
            Study Scope: Analyzed Demographics
          </h2>
          <p className="text-sm text-[#AAAAAA]" data-id="element-170">
            Distribution of the 279,000+ unique individuals analyzed based on
            impressions
          </p>
        </div>

        <DemographicCharts genderData={genderData} ageData={ageData} data-id="element-171" />

        <div className="mt-8 p-4 bg-[#F9FAFB] rounded-[4px] border border-[#E5E5E5]" data-id="element-172">
          <div className="flex items-start gap-3" data-id="element-173">
            <UsersIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" data-id="element-174" />
            <p className="text-sm text-[#000000] leading-relaxed" data-id="element-175">
              <span className="font-bold text-[#101010]" data-id="element-176">Key Finding:</span> The
              study successfully captured a mature audience, with the largest
              identified segment being{' '}
              <span className="font-bold text-[#101010]" data-id="element-177">65+ (25%)</span>,
              followed closely by{' '}
              <span className="font-bold text-[#171717]" data-id="element-178">55-64 (22%)</span>.
              This demographic distribution aligns perfectly with the Lenovo
              target market, validating the precision of our audience
              measurement approach.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Campaign Impact: Demographic Uplift */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5]" data-id="element-179">
        <div className="mb-8" data-id="element-180">
          <p className="text-sm text-[#AAAAAA] leading-relaxed mb-4" data-id="element-181">
            <span className="font-bold text-[#101010]" data-id="element-182">Campaign Impact:</span>{' '}
            Measuring engagement uplift across demographic segments compared to
            pre-campaign baseline (Index 100 = baseline engagement level).
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-183">
            <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-184">
              Campaign Impact: Demographic Uplift
            </h2>
            <span className="self-start sm:self-auto text-xs font-bold text-[#C00000] bg-[#C00000]/10 px-3 py-1.5 rounded-sm" data-id="element-185">
              Uplift Index
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-186">
          {/* Gender Uplift */}
          <div data-id="element-187">
            <h3 className="text-lg font-bold text-[#101010] mb-6" data-id="element-188">
              Gender Uplift
            </h3>
            <div className="space-y-6" data-id="element-189">
              {[{
              name: 'Male',
              index: 143,
              label: 'High Impact',
              isTop: true
            }, {
              name: 'Female',
              index: 104,
              label: 'Stable',
              isTop: false
            }].map((segment, idx) => <div key={idx} data-id="element-190">
                  <div className="flex items-center justify-between mb-2" data-id="element-191">
                    <div className="flex items-center gap-2" data-id="element-192">
                      <span className="text-sm font-bold text-[#101010]" data-id="element-193">
                        {segment.name}
                      </span>
                      {segment.isTop && <span className="text-[10px] font-bold text-[#FFFFFF] bg-[#C00000] px-2 py-0.5 rounded-sm" data-id="element-194">
                          HIGH IMPACT
                        </span>}
                    </div>
                    <span className={`text-sm font-bold ${segment.isTop ? 'text-[#C00000]' : 'text-[#AAAAAA]'}`} data-id="element-195">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-[#F9FAFB] rounded-full h-3 overflow-hidden" data-id="element-196">
                    <motion.div className={`h-3 rounded-full ${segment.isTop ? 'bg-[#C00000]' : 'bg-[#AAAAAA]'}`} initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 150 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-197" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-198">
                    <p className="text-xs text-[#AAAAAA] font-medium" data-id="element-199">
                      {segment.index > 100 ? `${segment.index - 100}% above baseline` : segment.index === 100 ? 'At baseline' : 'Below baseline'}
                    </p>
                    <p className="text-xs text-[#AAAAAA] italic" data-id="element-200">
                      {segment.label}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-[#C00000]/5 rounded-[4px] border border-[#C00000]/20" data-id="element-201">
              <div className="flex items-start gap-3" data-id="element-202">
                <TrendingUpIcon className="h-5 w-5 text-[#C00000] mt-0.5 flex-shrink-0" data-id="element-203" />
                <p className="text-sm text-[#000000] leading-relaxed" data-id="element-204">
                  <span className="font-bold text-[#101010]" data-id="element-205">Key Insight:</span>{' '}
                  The TV campaign had the strongest activation effect on{' '}
                  <span className="font-bold text-[#C00000]" data-id="element-206">Men</span>. While
                  women had high baseline interest, Men showed a{' '}
                  <span className="font-bold text-[#101010]" data-id="element-207">43% increase</span>{' '}
                  in engagement triggered by the flight.
                </p>
              </div>
            </div>
          </div>

          {/* Age Uplift */}
          <div data-id="element-208">
            <h3 className="text-lg font-bold text-[#101010] mb-6" data-id="element-209">
              Age Uplift
            </h3>
            <div className="space-y-6" data-id="element-210">
              {[{
              name: '65+',
              index: 125,
              label: 'Top Driver',
              isTop: true
            }, {
              name: '55-64',
              index: 115,
              label: 'Strong',
              isTop: false
            }, {
              name: '35-54',
              index: 105,
              label: 'Moderate',
              isTop: false
            }].map((segment, idx) => <div key={idx} data-id="element-211">
                  <div className="flex items-center justify-between mb-2" data-id="element-212">
                    <div className="flex items-center gap-2" data-id="element-213">
                      <span className="text-sm font-bold text-[#101010]" data-id="element-214">
                        {segment.name}
                      </span>
                      {segment.isTop && <span className="text-[10px] font-bold text-[#FFFFFF] bg-[#C00000] px-2 py-0.5 rounded-sm" data-id="element-215">
                          TOP DRIVER
                        </span>}
                    </div>
                    <span className={`text-sm font-bold ${segment.isTop ? 'text-[#C00000]' : 'text-[#AAAAAA]'}`} data-id="element-216">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-[#F9FAFB] rounded-full h-3 overflow-hidden" data-id="element-217">
                    <motion.div className={`h-3 rounded-full ${segment.isTop ? 'bg-[#C00000]' : 'bg-[#AAAAAA]'}`} initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 150 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-218" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-219">
                    <p className="text-xs text-[#AAAAAA] font-medium" data-id="element-220">
                      {segment.index > 100 ? `${segment.index - 100}% above baseline` : 'At baseline'}
                    </p>
                    <p className="text-xs text-[#AAAAAA] italic" data-id="element-221">
                      {segment.label}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-[#C00000]/5 rounded-[4px] border border-[#C00000]/20" data-id="element-222">
              <div className="flex items-start gap-3" data-id="element-223">
                <TrendingUpIcon className="h-5 w-5 text-[#C00000] mt-0.5 flex-shrink-0" data-id="element-224" />
                <p className="text-sm text-[#000000] leading-relaxed" data-id="element-225">
                  <span className="font-bold text-[#101010]" data-id="element-226">Key Insight:</span>{' '}
                  The campaign successfully activated the older target
                  demographics, showing the highest uplift among the{' '}
                  <span className="font-bold text-[#C00000]" data-id="element-227">65+ segment</span>{' '}
                  with a{' '}
                  <span className="font-bold text-[#101010]" data-id="element-228">25% increase</span>{' '}
                  above baseline.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Statistical Proof & Audience Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:gap-6" data-id="element-229">
        {/* Statistical Proof Column */}
        <motion.div id="proof" variants={itemVariants} className="space-y-8 flex flex-col" data-id="element-230">
          <div className="flex flex-col gap-6" data-id="element-231">
            <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-232">
              Statistical Proof
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-233">
              {[{
              icon: ShieldCheckIcon,
              title: 'Lift Certainty',
              value: '97.5%',
              desc: 'Probability that the campaign uplift during flight was caused by the campaign and not by chance.'
            }, {
              icon: BarChart3Icon,
              title: 'Statistical Validation',
              value: 'p < 0.05',
              desc: 'The uplift is statistically validated, not due to random chance.'
            }].map((stat, idx) => <div key={idx} className="bg-[#C00000]/5 p-6 rounded-[4px] border border-[#C00000]/20 print:bg-[#FFFFFF] print:border print:border-[#E5E5E5] h-full" data-id="element-234">
                  <div className="flex items-center gap-3 mb-4" data-id="element-235">
                    <stat.icon className="h-5 w-5 text-[#C00000]" data-id="element-236" />
                    <h3 className="text-base font-bold text-[#101010]" data-id="element-237">
                      {stat.title}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-[#101010] mb-3" data-id="element-238">
                    {stat.value}
                  </p>
                  <p className="text-sm text-[#000000] leading-relaxed" data-id="element-239">
                    {stat.desc}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Premium Publisher Showcase */}
          <div className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] flex-1" data-id="element-240">
            <div className="mb-8" data-id="element-241">
              <div className="flex items-center gap-3 mb-4" data-id="element-242">
                <h2 className="text-xl font-bold text-[#101010]" data-id="element-243">
                  Premium Measurement Environment
                </h2>
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.8,
                type: 'spring',
                stiffness: 200
              }} data-id="element-244">
                  <CheckCircleIcon className="h-5 w-5 text-[#AAAAAA]" data-id="element-245" />
                </motion.div>
              </div>
              <p className="text-sm text-[#000000] leading-relaxed" data-id="element-246">
                Measurement took place at the heart of Dutch quality publishers.
                We see a stable presence on A-brands such as RTL, IEX, and major
                lifestyle titles, which guarantees that the measured resonance
                comes from real, engaged consumers.
              </p>
            </div>

            {/* Category Groups */}
            <div className="space-y-6" data-id="element-247">
              {[{
              key: 'News',
              label: 'News & Current Affairs',
              badge: 'Trusted Information',
              color: 'bg-[#C00000]/10 text-[#C00000]',
              icon: NewspaperIcon,
              iconColor: 'text-[#C00000]'
            }, {
              key: 'Food',
              label: 'Food & Lifestyle',
              badge: 'High Engagement',
              color: 'bg-[#F9FAFB] text-[#AAAAAA]',
              icon: UtensilsIcon,
              iconColor: 'text-[#AAAAAA]'
            }, {
              key: 'Sport',
              label: 'Sport & Automotive',
              badge: 'Active 50+ Male',
              color: 'bg-[#F9FAFB] text-[#AAAAAA]',
              icon: BikeIcon,
              iconColor: 'text-[#AAAAAA]'
            }, {
              key: 'Radio',
              label: 'Radio & Weather',
              badge: 'Contextual Relevance',
              color: 'bg-[#F9FAFB] text-[#AAAAAA]',
              icon: ActivityIcon,
              iconColor: 'text-[#AAAAAA]'
            }, {
              key: 'Finance',
              label: 'Financial',
              badge: 'Affluent Audience',
              color: 'bg-[#F9FAFB] text-[#AAAAAA]',
              icon: CoinsIcon,
              iconColor: 'text-[#AAAAAA]'
            }].map(category => <div key={category.key} data-id="element-248">
                  <div className="flex items-center gap-2 mb-3" data-id="element-249">
                    <div className={`px-3 py-1.5 ${category.color} rounded-sm`} data-id="element-250">
                      <span className="text-xs font-bold uppercase tracking-wide" data-id="element-251">
                        {category.label}
                      </span>
                    </div>
                    <span className="text-xs text-[#AAAAAA]" data-id="element-252">
                      {category.badge}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3" data-id="element-253">
                    {publishers.filter(pub => pub.category === category.key).slice(0, expandedCategories[category.key as keyof typeof expandedCategories] ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.9 + idx * 0.05
                }} className="bg-[#FFFFFF] p-4 rounded-[4px] border border-[#E5E5E5] print:bg-[#FFFFFF]" data-id="element-254">
                          <div className="flex items-center gap-3" data-id="element-255">
                            <div className="p-2 bg-[#FFFFFF] rounded-[4px] border border-[#E5E5E5] flex-shrink-0" data-id="element-256">
                              <category.icon className={`h-5 w-5 ${category.iconColor}`} data-id="element-257" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-258">
                              <p className="text-sm font-bold text-[#101010] truncate" data-id="element-259">
                                {pub.name}
                              </p>
                              <p className="text-xs text-[#AAAAAA] truncate" data-id="element-260">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>)}
                  </div>
                  {publishers.filter(pub => pub.category === category.key).length > 2 && <button onClick={() => toggleCategory(category.key as any)} className={`mt-3 flex items-center gap-1 text-sm ${category.color.split(' ')[1]} hover:opacity-80 transition-opacity print:hidden`} data-id="element-261">
                      <span data-id="element-262">
                        {expandedCategories[category.key as keyof typeof expandedCategories] ? 'Show less' : `Show ${publishers.filter(pub => pub.category === category.key).length - 2} more`}
                      </span>
                      {expandedCategories[category.key as keyof typeof expandedCategories] ? <ChevronUpIcon className="h-4 w-4" data-id="element-263" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-264" />}
                    </button>}
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Audience Insights Column */}
        <motion.div id="audience" variants={itemVariants} className="space-y-8 flex flex-col" data-id="element-265">
          {/* Audience Resonance */}
          <div className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5]" data-id="element-266">
            <div className="mb-8" data-id="element-267">
              <p className="text-sm text-[#AAAAAA] leading-relaxed mb-4" data-id="element-268">
                <span className="font-bold text-[#101010]" data-id="element-269">
                  Audience Resonance:
                </span>{' '}
                Identifying which interest segments reacted most strongly to the
                campaign stimulus.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-270">
                <h2 className="text-xl font-bold text-[#101010]" data-id="element-271">
                  Audience Resonance
                </h2>
                <span className="self-start sm:self-auto text-xs font-bold text-[#C00000] bg-[#C00000]/10 px-3 py-1.5 rounded-sm" data-id="element-272">
                  Who responded?
                </span>
              </div>
            </div>

            <div className="space-y-6" data-id="element-273">
              {audienceSegments.map((segment, idx) => <div key={idx} data-id="element-274">
                  <div className="flex items-center justify-between mb-2" data-id="element-275">
                    <div className="flex items-center gap-2" data-id="element-276">
                      <span className="text-sm font-bold text-[#101010]" data-id="element-277">
                        {segment.name}
                      </span>
                      {idx === 0 && <span className="text-[10px] font-bold text-[#FFFFFF] bg-[#C00000] px-2 py-0.5 rounded-sm" data-id="element-278">
                          TOP SEGMENT
                        </span>}
                    </div>
                    <span className="text-sm font-bold text-[#C00000]" data-id="element-279">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-[#F9FAFB] rounded-full h-3 overflow-hidden" data-id="element-280">
                    <motion.div className="h-3 rounded-full bg-[#C00000]" initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 250 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1,
                  ease: 'easeOut'
                }} data-id="element-281" />
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-282">
                    <p className="text-xs text-[#AAAAAA] font-medium" data-id="element-283">
                      {segment.index > 100 ? `${segment.index - 100}% above average interest` : 'Average interest'}
                    </p>
                    <p className="text-xs text-[#AAAAAA] italic" data-id="element-284">
                      {segment.index > 100 && `${(segment.index / 100).toFixed(1)}x more interested`}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-[#F9FAFB] rounded-[4px] border border-[#E5E5E5] print:bg-[#FFFFFF]" data-id="element-285">
              <div className="flex items-start gap-3" data-id="element-286">
                <ArrowRightIcon className="h-5 w-5 text-[#C00000] mt-0.5 flex-shrink-0" data-id="element-287" />
                <p className="text-sm text-[#000000] leading-relaxed" data-id="element-288">
                  <span className="font-bold text-[#101010]" data-id="element-289">Insight:</span> The
                  campaign resonated most strongly with users actively
                  interested in{' '}
                  <span className="font-bold text-[#C00000]" data-id="element-290">
                    Pension Planning
                  </span>{' '}
                  (Index 250), confirming the relevance for the 50+ demographic.
                </p>
              </div>
            </div>
          </div>

          {/* Audience Interests Revealed */}
          <div className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] flex-1" data-id="element-291">
            <div className="mb-8" data-id="element-292">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4" data-id="element-293">
                <h2 className="text-xl font-bold text-[#101010]" data-id="element-294">
                  Audience Interests Revealed
                </h2>
                <span className="self-start sm:self-auto text-xs font-bold text-[#C00000] bg-[#C00000]/10 px-3 py-1.5 rounded-sm" data-id="element-295">
                  Who are they?
                </span>
              </div>
              <p className="text-sm text-[#000000] leading-relaxed" data-id="element-296">
                Beyond "Lenovo" as a broad demographic, behavioral data reveals
                three distinct sub-profiles based on actual content engagement
                patterns.
              </p>
            </div>

            <div className="space-y-6" data-id="element-297">
              {audiencePersonas.map((persona, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8 + idx * 0.15,
              type: 'spring'
            }} className="relative bg-[#F9FAFB] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-298">
                  <div className="absolute top-4 right-4" data-id="element-299">
                    <div className="px-3 py-1 rounded-sm bg-[#C00000]/10 border border-[#C00000]/20" data-id="element-300">
                      <span className="text-xs font-bold text-[#C00000]" data-id="element-301">
                        Index {persona.index}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-302">
                    <div className="flex-shrink-0 p-3 rounded-[4px] bg-[#FFFFFF] border border-[#E5E5E5]" data-id="element-303">
                      {persona.icon === 'wine' && <WineIcon className="h-5 w-5 text-[#101010]" data-id="element-304" />}
                      {persona.icon === 'newspaper' && <NewspaperIcon className="h-5 w-5 text-[#101010]" data-id="element-305" />}
                      {persona.icon === 'coins' && <CoinsIcon className="h-5 w-5 text-[#101010]" data-id="element-306" />}
                    </div>

                    <div className="flex-1 pr-12" data-id="element-307">
                      <h3 className="text-base font-bold text-[#101010] mb-1" data-id="element-308">
                        {persona.name}
                      </h3>
                      <p className="text-xs font-medium mb-2 text-[#AAAAAA]" data-id="element-309">
                        {persona.tagline}
                      </p>
                      <p className="text-xs text-[#000000] leading-relaxed mb-3" data-id="element-310">
                        {persona.description}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-311">
                        {persona.interests.slice(0, 3).map((interest, i) => <span key={i} className="text-[10px] px-2 py-1 rounded-sm bg-[#FFFFFF] border border-[#E5E5E5] text-[#101010] font-medium" data-id="element-312">
                            {interest}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-8 p-4 bg-[#C00000]/5 rounded-[4px] border border-[#C00000]/20" data-id="element-313">
              <div className="flex items-start gap-3" data-id="element-314">
                <LightbulbIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" data-id="element-315" />
                <div data-id="element-316">
                  <p className="text-sm font-bold text-[#101010] mb-2" data-id="element-317">
                    Strategic Implication
                  </p>
                  <p className="text-sm text-[#000000] leading-relaxed" data-id="element-318">
                    The strongest resonance lies with{' '}
                    <span className="font-bold text-[#101010]" data-id="element-319">
                      The Smart Investor
                    </span>{' '}
                    (Index 250) and{' '}
                    <span className="font-bold text-[#171717]" data-id="element-320">
                      The Bon Vivant
                    </span>{' '}
                    (Index 185). Future campaigns should position Lenovo as the
                    smart choice that frees up money for what matters most,
                    whether that's securing your family's financial future
                    through investments and planning, or enjoying the lifestyle
                    you've earned through travel and experiences. This audience
                    values financial intelligence: they're not just saving
                    money, they're strategically reallocating it toward their
                    priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Key Insight Section */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] print:break-inside-avoid" data-id="element-321">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-6 mb-8" data-id="element-322">
          <div className="flex items-start gap-4 flex-1" data-id="element-323">
            <LightbulbIcon className="h-7 w-7 text-[#C00000] flex-shrink-0 mt-1" data-id="element-324" />
            <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-325">
              Key Insight: TV Built Lasting Brand Equity, Not Just Temporary
              Awareness
            </h2>
          </div>
          <ShareButton title="Key Insight: TV Built Lasting Brand Equity, Not Just Temporary Awareness" text="The Lenovo campaign didn't just drive a +33% spike—it created a sustained +14% lift that persisted weeks after the campaign ended. This represents a structural investment in brand value that compounds over time, with 97.5% certainty." data-id="element-326" />
        </div>

        <div className="space-y-5 text-base text-[#000000] leading-relaxed" data-id="element-327">
          <p data-id="element-328">
            This test validates TV as a{' '}
            <span className="font-bold text-[#101010]" data-id="element-329">
              brand-building engine
            </span>
            , not just an awareness channel. The campaign generated a{' '}
            <span className="font-bold text-[#101010]" data-id="element-330">
              validated +33% uplift during flight
            </span>
            , and importantly, brand interest remained{' '}
            <span className="font-bold text-[#101010]" data-id="element-331">
              +14% elevated post-flight
            </span>
            , a promising indicator of lasting brand equity. This structural
            lift means every future Euro spent starts from a higher efficiency
            level. It's not just marketing cost; it's{' '}
            <span className="font-bold text-[#101010]" data-id="element-332">
              capital investment in brand value
            </span>{' '}
            that compounds over time.
          </p>

          <p data-id="element-333">
            The data reveals two critical insights: First, we detected{' '}
            <span className="font-bold text-[#101010]" data-id="element-334">
              zero "warm-up period"
            </span>
            , the curve spiked immediately at campaign launch. This validates
            that the creative message was extremely clear and urgent to the
            target audience. Second, the strongest resonance came from{' '}
            <span className="font-bold text-[#101010]" data-id="element-335">
              The Smart Investor persona
            </span>{' '}
            (Index 250), users actively researching pension planning and
            financial optimization. This audience isn't just saving money;
            they're strategically reallocating it toward their priorities.
          </p>

          <p data-id="element-336">
            With{' '}
            <span className="font-bold text-[#101010]" data-id="element-337">
              97.5% lift certainty
            </span>{' '}
            and statistical validation (p &lt; 0.05), we can confidently say
            this campaign didn't just drive temporary interest, it fundamentally
            shifted how the 50+ market perceives Lenovo. The strategic
            opportunity now is to maintain this elevated baseline while
            optimizing creative and frequency to maximize ROI on future flights.
          </p>
        </div>
      </motion.div>

      {/* Recommendations & Next Steps */}
      <motion.div variants={itemVariants} className="bg-[#FFFFFF] p-8 rounded-[4px] border border-[#E5E5E5] print:border print:border-[#E5E5E5] print:break-inside-avoid" data-id="element-338">
        <div className="flex items-start gap-4 mb-8" data-id="element-339">
          <SparklesIcon className="h-7 w-7 text-[#C00000] flex-shrink-0 mt-1" data-id="element-340" />
          <h2 className="text-xl md:text-2xl font-bold text-[#101010]" data-id="element-341">
            Recommendations & Next Steps
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:gap-4" data-id="element-342">
          {[{
          icon: CoinsIcon,
          title: 'Protect Brand Equity',
          text: 'Maintain consistent TV presence to preserve the +14% elevated baseline. Set up continuous measurement to track brand equity evolution and optimize maintenance frequency.',
          note: 'Extending post-flight observation to 3-4 weeks in your next test increases statistical power and improves the likelihood of reaching 90-95% confidence on sustained impact.'
        }, {
          icon: SparklesIcon,
          title: 'Optimize Flight Strategy',
          text: 'The zero warm-up period suggests opportunity to test shorter, more frequent bursts vs. sustained flights to maximize efficiency and ROI. Consider pre-testing creative variants to identify the strongest performer before launch.',
          link: 'https://create.brandpulse-analytics.com/?type=3#uploadVideo',
          linkText: 'pre-testing creative variants'
        }, {
          icon: UsersIcon,
          title: 'Target Smart Investor',
          text: 'Focus on The Smart Investor persona (Index 250) with messaging that positions Lenovo as financially intelligent. Test persona-specific creative variants in Q2.'
        }].map((rec, idx) => <div key={idx} className="bg-[#F9FAFB] p-6 rounded-[4px] border border-[#E5E5E5] print:bg-[#FFFFFF] print:border print:border-[#E5E5E5] h-full flex flex-col" data-id="element-343">
              <div className="flex items-center gap-3 mb-4" data-id="element-344">
                <div className="p-2.5 bg-[#FFFFFF] rounded-[4px] border border-[#E5E5E5] print:border-none" data-id="element-345">
                  <rec.icon className="h-5 w-5 text-[#C00000]" data-id="element-346" />
                </div>
                <h3 className="text-base font-bold text-[#101010]" data-id="element-347">
                  {rec.title}
                </h3>
              </div>
              <p className="text-sm text-[#000000] leading-relaxed mb-3 flex-grow" data-id="element-348">
                {rec.link ? <>
                    {rec.text.split(rec.linkText!)[0]}
                    <a href={rec.link} target="_blank" rel="noopener noreferrer" className="text-[#C00000] hover:underline font-bold" data-id="element-349">
                      {rec.linkText}
                    </a>
                    {rec.text.split(rec.linkText!)[1]}
                  </> : rec.text}
              </p>
              {rec.note && <p className="text-xs text-[#AAAAAA] italic bg-[#FFFFFF] p-3 rounded-[4px] border border-[#E5E5E5] mt-auto" data-id="element-350">
                  <span className="font-bold text-[#101010]" data-id="element-351">
                    Future opportunity:
                  </span>{' '}
                  {rec.note}
                </p>}
            </div>)}
        </div>

        <div className="bg-[#F9FAFB] p-6 rounded-[4px] border border-[#E5E5E5] print:bg-[#FFFFFF] print:border print:border-[#E5E5E5]" data-id="element-352">
          <p className="text-base text-[#000000] leading-relaxed mb-4" data-id="element-353">
            This test proves TV created a{' '}
            <span className="font-bold text-[#101010]" data-id="element-354">
              structural investment in brand value
            </span>
            , not just temporary awareness. The sustained +14% post-flight
            effect represents brand equity that compounds over time. With
            validated campaign lift and observed post-flight durability, protect
            this gain through consistent presence, amplify what works by
            targeting The Smart Investor persona, and optimize efficiency
            through strategic testing. For your next flight, consider{' '}
            <a href="https://create.brandpulse-analytics.com/?type=3#uploadVideo" target="_blank" rel="noopener noreferrer" className="text-[#C00000] hover:underline font-bold" data-id="element-355">
              pre-testing creative concepts
            </a>{' '}
            to predict which will deliver the highest brand resonance.
          </p>
          <div className="flex items-center justify-end" data-id="element-356">
            <div className="flex items-center gap-2 text-sm" data-id="element-357">
              <ShieldCheckIcon className="h-4 w-4 text-[#C00000]" data-id="element-358" />
              <span className="text-[#AAAAAA] font-medium" data-id="element-359">
                {metrics.confidence}% Lift Certainty
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Confidence Notification */}
      <ConfidenceNotification uplift={metrics.uplift} confidence={metrics.confidence} data-id="element-360" />
    </motion.div>;
};